<script setup lang="ts">
  import DropzoneArea from './DropzoneArea.vue';
  import FileDataList from './FileDataList.vue';
  import FileList from './FileList.vue';
  import { ColoredButton } from '@/shared/ui';
  import PlusIcon from '@/shared/assets/icons/PlusIcon.svg';
  import { useFileDropzone } from '@/shared/lib';
  import { useFileValidation, type InvalidFile } from '../model';
  import { useFileUploadStore } from '@/features/Document';
  import { useCustomToast } from '@/shared/lib';
  import { nextTick } from 'vue';
  import { storeToRefs } from 'pinia';
  import {
    MAX_FILES_PER_UPLOAD,
    FILE_COUNT_EXCEEDS_LIMIT,
    FILE_COUNT_LIMIT_REACHED,
    DUPLICATE_PREFIXES_IN_BATCH_MESSAGE,
  } from '@/shared/config';

  const toast = useCustomToast();

  const fileUploadStore = useFileUploadStore();
  const { files } = storeToRefs(fileUploadStore);

  const props = defineProps<{
    filesLoaded: boolean;
    multiple: boolean;
    replacementContext?: { documentId: number; revisionId: number };
    isExternal?: boolean;
    isReplacement?: boolean;
    documentName?: string;
    isQualityRecord?: boolean;
  }>();

  const emit = defineEmits<{
    (e: 'filesSelected', files: File[], isExternal?: boolean, documentName?: string): void;
    (e: 'uploadProgress', fileId: string, progress: number): void;
    (e: 'uploadComplete', fileId: string, documentIds?: number[]): void;
    (e: 'allChangelogsFilled', filled: boolean): void;
  }>();

  const { fileInput, isDragging, openFileDialog, onDragEnter, onDragLeave, onDragOver, onDrop } =
    useFileDropzone();
  const {
    filterValidDocumentFiles,
    showValidationErrors,
    getAcceptAttribute,
    getDuplicatePrefixesInBatch,
  } = useFileValidation();
  const {
    handleFilesSelected,
    getFileProgress,
    getFileStatus,
    removeFile,
    uploadedDocumentIds,
    getDocumentIdForFile,
    updateFileChangelog,
  } = fileUploadStore;

  const clearFileInput = () => {
    if (fileInput.value) fileInput.value.value = '';
  };

  const onFilesSelectedHandler = (newFiles: FileList | null) => {
    if (!newFiles || newFiles.length === 0) return;

    const currentFilesCount = files.value.length;
    const maxAllowedFiles = MAX_FILES_PER_UPLOAD;

    if (currentFilesCount >= maxAllowedFiles) {
      toast.showErrorToast(
        {
          title: 'Limite de arquivos atingido',
          message: FILE_COUNT_LIMIT_REACHED(maxAllowedFiles),
          type: 'error',
        },
        { timeout: 5000 }
      );
      clearFileInput();
      return;
    }

    const { valid, invalid } = filterValidDocumentFiles(newFiles, props.isExternal);

    if (invalid.length > 0) {
      showValidationErrors(invalid as InvalidFile[]);
    }

    if (valid.length === 0) {
      nextTick(clearFileInput);
      return;
    }

    const availableSlots = maxAllowedFiles - currentFilesCount;

    if (valid.length > availableSlots) {
      toast.showErrorToast(
        {
          title: 'Limite de arquivos excedido',
          message: FILE_COUNT_EXCEEDS_LIMIT(maxAllowedFiles),
          type: 'error',
        },
        { timeout: 5000 }
      );
      clearFileInput();
      return;
    }

    if (!props.isExternal) {
      const combined = [...files.value, ...valid];
      const duplicatePrefixes = getDuplicatePrefixesInBatch(combined);
      if (duplicatePrefixes.length > 0) {
        toast.showErrorToast(
          {
            title: 'Prefixos duplicados',
            message: DUPLICATE_PREFIXES_IN_BATCH_MESSAGE,
            type: 'error',
          },
          { timeout: 6000 }
        );
        clearFileInput();
        return;
      }
    }

    const mockFileList = {
      ...valid,
      length: valid.length,
      item: (index: number) => valid[index],
    } as FileList;

    handleFilesSelected(
      mockFileList,
      {
        filterFiles: (files: FileList) => valid,
        showError: (message: string) => {
          console.error(message);
        },
        onFilesSelected: (selectedFiles: File[]) =>
          emit('filesSelected', selectedFiles, props.isExternal, props.documentName),
      },
      props.isExternal,
      props.documentName
    );

    clearFileInput();
  };

  const handleDrop = (e: DragEvent) => {
    const droppedFiles = onDrop(e);
    if (droppedFiles) {
      onFilesSelectedHandler(droppedFiles);
    }
  };

  const handleFileDeleted = (documentId: number) => {
    const file = files.value.find((file) => getDocumentIdForFile(file) === documentId) || -1;

    removeFile(file);
    emit('uploadComplete', '', uploadedDocumentIds);
  };

  const handleChangelogUpdated = (file: File, changelog: string) => {
    updateFileChangelog(file, changelog);
  };

  const handleAllChangelogsFilled = (filled: boolean) => {
    emit('allChangelogsFilled', filled);
  };
</script>

<template>
  <div>
    <DropzoneArea
      v-if="files.length === 0"
      :is-dragging="isDragging"
      :on-drag-enter="onDragEnter"
      :on-drag-leave="onDragLeave"
      :on-drag-over="onDragOver"
      :on-drop="handleDrop"
      :open-file-dialog="openFileDialog"
      :multiple="multiple"
    />

    <div v-if="files.length > 0 && multiple" class="flex justify-end mb-5">
      <ColoredButton
        :icon="PlusIcon"
        text="Adicionar documento"
        data-testid="add-document-button"
        @click="openFileDialog"
      />
    </div>

    <FileDataList
      v-show="filesLoaded && !isQualityRecord"
      :files="files"
      :get-file-progress="getFileProgress"
      :get-file-status="getFileStatus"
      :document-ids="uploadedDocumentIds"
      :get-document-id-for-file="getDocumentIdForFile"
      :multiple="multiple"
      :is-external="isExternal"
      :is-replacement="isReplacement"
      @file-deleted="handleFileDeleted"
      @changelog-updated="handleChangelogUpdated"
      @all-changelogs-filled="handleAllChangelogsFilled"
    />

    <input
      ref="fileInput"
      type="file"
      :multiple="multiple"
      :accept="getAcceptAttribute()"
      class="hidden"
      @change="onFilesSelectedHandler(($event.target as HTMLInputElement).files)"
    />
  </div>
</template>
